import { FC, useEffect, useState } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip, Grid, IconButton, MenuItem, Paper, TextField, Tooltip, Typography } from "@mui/material";
import NavigateBeforeOutlinedIcon from "@mui/icons-material/NavigateBeforeOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LoadingButton from "@mui/lab/LoadingButton";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { ApiStatus } from "../../utils/api.types";
import LocationSelect from "./pickers/LocationSelect";
import SkuSelect from "./pickers/SkuSelect";
import BatchSelect from "./pickers/BatchSelect";
import { useUnitName, unitOptionTokens, factorOf, baseUnitNameOf } from "./pickers/useUnitName";
import {
  addTransfer,
  cancelTransfer,
  dispatchTransfer,
  fetchTransferById,
  receiveTransfer,
  reverseTransfer,
  updateTransfer,
  transferStockCheck,
} from "../../store/stockTransferStore/stockTransferThunk";

const unitOptionsFor = unitOptionTokens;
const emptyRow = () => ({ sku: null as any, skuId: "", unit: "", qty: "", batchId: "", reqItemId: "" });

const TransferForm: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEditing = !!id;
  const { status } = useAppSelector((state) => state.stockTransfers);
  const unitName = useUnitName();

  const [fromLocationId, setFromLocationId] = useState("");
  const [toLocationId, setToLocationId] = useState("");
  const [items, setItems] = useState<any[]>([emptyRow()]);
  const [docStatus, setDocStatus] = useState("draft");
  const [stockCheck, setStockCheck] = useState<any>(null);
  const [requisitionNumber, setRequisitionNumber] = useState<string | null>(null);
  const [transferNumber, setTransferNumber] = useState<string | null>(null);
  // Reverse lock: a dispatched/received transfer can only be reversed while the
  // stock it moved is still intact. The backend tells us if it's been consumed at
  // the destination so we can disable the button proactively.
  const [canReverse, setCanReverse] = useState(false);
  const [reverseBlockReason, setReverseBlockReason] = useState<string>("");

  const isDraft = !isEditing || docStatus === "draft";

  // Live check: does the SOURCE location actually have the stock for these lines?
  // (debounced so it re-runs as the source/items/units/qty/batch change)
  useEffect(() => {
    const ready = fromLocationId && items.some((r) => r.skuId && Number(r.qty) > 0);
    if (!ready) {
      setStockCheck(null);
      return;
    }
    const t = setTimeout(() => {
      dispatch(
        transferStockCheck({
          fromLocationId,
          items: items
            .filter((r) => r.skuId && Number(r.qty) > 0)
            .map((r) => ({ skuId: r.skuId, qty: Number(r.qty), unit: r.unit, batchId: r.batchId || null })),
        })
      )
        .unwrap()
        .then((data: any) => setStockCheck(data))
        .catch(() => setStockCheck(null));
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromLocationId, items]);

  const shortLines = (stockCheck?.lines || []).filter((l: any) => l.tracked && !l.sufficient);

  useEffect(() => {
    if (isEditing && id) {
      dispatch(fetchTransferById(id))
        .unwrap()
        .then((t: any) => {
          setFromLocationId(t.fromLocationId?._id || t.fromLocationId || "");
          setToLocationId(t.toLocationId?._id || t.toLocationId || "");
          setDocStatus(t.status || "draft");
          setRequisitionNumber(t.requisitionNumber || null);
          setTransferNumber(t.transferNumber || null);
          setCanReverse(!!t.canReverse);
          setReverseBlockReason(t.reverseBlockReason || "");
          setItems(
            (t.items || []).map((it: any) => ({
              sku: it.skuId,
              skuId: it.skuId?._id || it.skuId,
              unit: it.unit || "",
              qty: it.qty ?? "",
              batchId: it.batchId?._id || it.batchId || "",
              // Carry the source requisition line through edits — the backend credits
              // qtyIssued by this exact line; dropping it here reverts to skuId guessing.
              reqItemId: it.reqItemId || "",
              // Requisition context (requested/approved/issued) for this line, if any.
              reqLine: it.reqLine || null,
            }))
          );
        })
        .catch((e: any) => toast.error(e?.message || "Failed to load transfer"));
    }
  }, [id, isEditing, dispatch]);

  const updateRow = (idx: number, patch: any) => setItems((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const onSkuChange = (idx: number, sku: any) => updateRow(idx, { sku, skuId: sku?._id || "", unit: unitOptionsFor(sku)[0] || "", batchId: "" });

  const buildPayload = () => ({
    fromLocationId,
    toLocationId,
    items: items
      .filter((r) => r.skuId && Number(r.qty) > 0)
      .map((r) => ({ skuId: r.skuId, qty: Number(r.qty), unit: r.unit || null, batchId: r.batchId || null, reqItemId: r.reqItemId || null })),
  });

  // Batch-tracked rows must carry a batch before dispatch (the backend rejects a
  // null batch). Returns the first offending item's name, or null if all good.
  const missingBatchItem = () => {
    const bad = items.find((r) => r.skuId && Number(r.qty) > 0 && r.sku?.isBatchTracked === true && !r.batchId);
    return bad ? bad.sku?.name || "this item" : null;
  };

  const onSave = async () => {
    if (!fromLocationId || !toLocationId) return toast.error("Select source and destination locations");
    if (fromLocationId === toLocationId) return toast.error("Source and destination must differ");
    const payload = buildPayload();
    if (!payload.items.length) return toast.error("Add at least one item");
    try {
      if (isEditing && id) {
        await dispatch(updateTransfer({ id, payload })).unwrap();
        navigate("/inventory/transfers");
      } else {
        const created = await dispatch(addTransfer(payload)).unwrap();
        navigate(`/inventory/transfers/${created._id}`);
      }
    } catch (e: any) {
      toast.error(e?.message || "Failed to save transfer");
    }
  };

  // Dispatch reads the SAVED transfer, not the in-memory form — so persist the
  // current batch/qty picks FIRST, then dispatch. Fixes the intermittent
  // "Select a batch ... before dispatch" when a batch was picked but not yet saved.
  const onDispatch = async () => {
    if (!id) return;
    const missing = missingBatchItem();
    if (missing) return toast.error(`Pick a batch for "${missing}" before dispatch.`);
    try {
      await dispatch(updateTransfer({ id, payload: buildPayload() })).unwrap();
      await dispatch(dispatchTransfer(id)).unwrap();
      navigate("/inventory/transfers");
    } catch (e: any) {
      // backend message is toasted globally by the axios interceptor
    }
  };

  const act = async (p: Promise<any>) => {
    try {
      await p;
      navigate("/inventory/transfers");
    } catch (e: any) {
      // Error is already toasted globally by the axios response interceptor (with
      // the real backend message). Just swallow it here so we don't navigate away.
    }
  };

  const loading = status === ApiStatus.LOADING;

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton size="small" onClick={() => navigate("/inventory/transfers")}>
            <NavigateBeforeOutlinedIcon />
          </IconButton>
          <Typography variant="h6">
            {isEditing ? `Stock Transfer${transferNumber ? ` ( ${transferNumber} )` : ""}` : "New Stock Transfer"}
          </Typography>
          {isEditing && <Chip size="small" label={(docStatus || "").replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase())} />}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          {isDraft && (
            <LoadingButton variant="contained" loading={loading} onClick={onSave}>
              {isEditing ? "Save" : "Create"}
            </LoadingButton>
          )}
          {isEditing && docStatus === "draft" && (
            <>
              <LoadingButton color="success" variant="contained" loading={loading} onClick={onDispatch}>
                Dispatch
              </LoadingButton>
              <Button color="error" variant="outlined" onClick={() => act(dispatch(cancelTransfer(id!)).unwrap())}>
                Cancel
              </Button>
            </>
          )}
          {isEditing && docStatus === "dispatched" && (
            <LoadingButton color="success" variant="contained" loading={loading} onClick={() => act(dispatch(receiveTransfer(id!)).unwrap())}>
              Receive
            </LoadingButton>
          )}
          {isEditing && (docStatus === "dispatched" || docStatus === "received") && (
            <Tooltip title={!canReverse ? reverseBlockReason || "This transfer cannot be reversed." : ""}>
              {/* span wrapper so the tooltip still shows on a disabled button */}
              <span>
                <LoadingButton
                  color="error"
                  variant="outlined"
                  loading={loading}
                  disabled={!canReverse}
                  onClick={() => {
                    if (window.confirm("Reverse this transfer? Stock returns to the source and is removed from the destination (a compensating entry is recorded).")) {
                      act(dispatch(reverseTransfer(id!)).unwrap());
                    }
                  }}
                >
                  Reverse
                </LoadingButton>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Accordion defaultExpanded disableGutters elevation={0} sx={{ mb: 2, border: "1px solid #e6e8ec", borderRadius: 2, "&:before": { display: "none" } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ display: "flex", alignItems: "center", gap: 0.5, fontWeight: 600 }}>
            <InfoOutlinedIcon sx={{ fontSize: 18 }} /> How to move stock between locations
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <ol style={{ margin: 0, paddingLeft: 18, color: "#3c4350", lineHeight: 1.7 }}>
            <li>Choose the <b>source</b> and <b>destination</b> locations.</li>
            <li>Add each item: the <b>unit</b> and <b>quantity</b> to move; for batch-tracked items pick the <b>batch</b> (only batches with stock at the source are listed).</li>
            <li><b>Create</b> the draft, then <b>Dispatch</b> — this removes stock from the source (goods are now in transit).</li>
            <li>When it arrives, open it and click <b>Receive</b> — this adds the same batch to the destination.</li>
          </ol>
        </AccordionDetails>
      </Accordion>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          {/* Exclude the chosen destination so source/destination can't be the same. */}
          <LocationSelect
            value={fromLocationId}
            onChange={(v) => { setFromLocationId(v); if (v && v === toLocationId) setToLocationId(""); }}
            label="From (source)"
            activeOnly={false}
            excludeId={toLocationId}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          {/* Exclude the chosen source so source/destination can't be the same. */}
          <LocationSelect
            value={toLocationId}
            onChange={(v) => { setToLocationId(v); if (v && v === fromLocationId) setFromLocationId(""); }}
            label="To (destination)"
            activeOnly={false}
            excludeId={fromLocationId}
          />
        </Grid>
      </Grid>

      {shortLines.length > 0 && (
        <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, border: "1px solid #f0c36d", backgroundColor: "#fff8e6", display: "flex", gap: 1 }}>
          <InfoOutlinedIcon sx={{ color: "#b8860b", fontSize: 20, mt: "2px" }} />
          <Box sx={{ fontSize: 13, color: "#7a5c00" }}>
            <b>The source location doesn't have enough stock to transfer.</b>{" "}
            <b>Dispatch will fail</b> (or post a flagged oversell) until you stock the source.
            <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
              {shortLines.map((l: any, i: number) => (
                <li key={l.skuId + "-" + i}>
                  <b>{l.name}</b>: need {l.needed} {l.baseUnit}, available {l.available} {l.baseUnit}
                  {l.batchScoped ? " in the chosen batch" : " at source"}
                  {" "}(short {Math.max(0, Number(l.needed) - Number(l.available))} {l.baseUnit})
                </li>
              ))}
            </ul>
          </Box>
        </Box>
      )}

      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
        <Typography sx={{ fontWeight: 600, mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
          Items to transfer
          <Tooltip title="Pick the item, the unit and quantity to move, and (for batch-tracked items) which batch to send.">
            <InfoOutlinedIcon sx={{ fontSize: 16, color: "#7a8290" }} />
          </Tooltip>
        </Typography>
        {items.map((row, idx) => {
          const factor = factorOf(row.sku, row.unit);
          const baseName = baseUnitNameOf(row.sku, unitName);
          const qtyNum = Number(row.qty);
          const baseQty = factor > 0 && qtyNum > 0 ? factor * qtyNum : 0;
          return (
            <Box key={idx} sx={{ mb: 1.5, pb: 1, borderBottom: "1px dashed #e6e8ec" }}>
              <Grid container spacing={1} sx={{ alignItems: "center" }}>
                <Grid item xs={12} sm={3.5}>
                  <SkuSelect value={row.skuId} onChange={(sku) => onSkuChange(idx, sku)} />
                </Grid>
                <Grid item xs={6} sm={1.6}>
                  <TextField select fullWidth size="small" label="Transfer unit" value={row.unit} onChange={(e) => updateRow(idx, { unit: e.target.value })} disabled={!isDraft}>
                    {unitOptionsFor(row.sku).map((u) => <MenuItem key={u} value={u}>{unitName(u)}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={6} sm={1.4}>
                  <TextField fullWidth size="small" type="number" label="Qty" value={row.qty} onChange={(e) => updateRow(idx, { qty: e.target.value })} disabled={!isDraft} />
                </Grid>
                <Grid item xs={10} sm={3}>
                  <BatchSelect skuId={row.skuId} locationId={fromLocationId} value={row.batchId} onChange={(b) => updateRow(idx, { batchId: b })} disabled={!isDraft} />
                </Grid>
                {isDraft && (
                  <Grid item xs={2} sm={0.6}>
                    <IconButton size="small" onClick={() => setItems((rows) => rows.filter((_, i) => i !== idx))}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Grid>
                )}
              </Grid>
              {factor > 0 && qtyNum > 0 && (
                <Typography sx={{ fontSize: 12, color: "#5b6472", mt: 0.5 }}>
                  Moves: {qtyNum} {unitName(row.unit)} × {factor} {baseName} = <b>{baseQty.toLocaleString()} {baseName}</b>
                </Typography>
              )}
              {row.reqLine && (
                <Typography sx={{ fontSize: 12, color: "#1f4e8a", mt: 0.25 }}>
                  Requisition{requisitionNumber ? ` ${requisitionNumber}` : ""}: approved{" "}
                  <b>{row.reqLine.qtyApproved ?? 0} {unitName(row.reqLine.unit)}</b>, issued {row.reqLine.qtyIssued ?? 0} ·{" "}
                  remaining <b>{Math.max(0, Number(row.reqLine.qtyApproved || 0) - Number(row.reqLine.qtyIssued || 0))} {unitName(row.reqLine.unit)}</b>
                </Typography>
              )}
            </Box>
          );
        })}
        {isDraft && (
          <Button size="small" startIcon={<AddIcon />} onClick={() => setItems((rows) => [...rows, emptyRow()])}
            sx={{ color: "#488dff", backgroundColor: "#fff", border: "0.5px solid #488dff", borderRadius: "7px", textTransform: "none", "&:hover": { backgroundColor: "#f5f9ff", border: "0.5px solid #488dff" } }}>
            Add item
          </Button>
        )}
      </Paper>
    </Box>
  );
};

export default TransferForm;
